// Presentation rules for broker fee plans (SM-839, SM-840) and, where
// the two share a rule, revenue-share plans (SM-841). Pure functions —
// the Fees tab, the Revenue share tab and their dialogs all read them,
// and the unit tests pin them — so "what does this rate mean", "may this
// time be sent", "did that write happen" and "what does this refusal
// say" are decided in one place.
//
// Every rule here is broker-service's first (decisions 705 and 708): it
// owns plan ownership, immutability, the rate range, the GOLD redemption
// ceiling, the root's FEE_CHANGE status and the resolution walk. The
// console mirrors the rules so a button that would only be refused is
// not offered as live, and explains the server's refusal when it comes.
// It never resolves which plan a node pays: that is always the fee
// schedule or the fee resolution, read from the server.

import { minorToPercent, percentToMinor } from "@/components/fees/FeeRateInput";
import { APIError } from "@/lib/api";
import type { BrokerItem, FeeAssignmentScope, FeePlanItem, FeePlanVersionItem } from "@/lib/broker-types";

import {
  BROKER_CURRENCIES,
  brokerErrorDetail,
  nodeCurrencies,
  nodeCurrencyRefusal,
  outcomeUnknown,
  type BrokerCurrency,
} from "./governance";

/** Broker plan rates are integer strings at this precision ("10000" = 1 %). */
export const FEE_RATE_PRECISION = 6;

/** 100 % at FEE_RATE_PRECISION (contracts brokerv1.FeeRateMax). */
export const FEE_RATE_MAX = 1_000_000;

/** The protected internal root (contracts brokerv1.SystemBrokerID). */
export const SYSTEM_BROKER_ID = "SYSTEM_BROKER";

/** Plan names are 1 to 200 characters (broker-service MaxNameLen). */
export const FEE_PLAN_NAME_MAX = 200;

/** The SYSTEM_BROKER root or a node of its tree: its plans are protected. */
export function isSystemBroker(b: Pick<BrokerItem, "broker_id" | "root_broker_id" | "is_system">): boolean {
  return Boolean(b.is_system) || b.broker_id === SYSTEM_BROKER_ID || b.root_broker_id === SYSTEM_BROKER_ID;
}

/** A root owns plans; a sub-broker only reads what resolves for it. */
export function isRootBroker(b: Pick<BrokerItem, "broker_id" | "root_broker_id" | "parent_broker_id">): boolean {
  return !b.parent_broker_id || b.broker_id === b.root_broker_id;
}

// ── rates ────────────────────────────────────────────────────────────

/** A rate as a human percentage string, "0" for zero ("2500" → "0.25"). */
export function rateToPercent(rate: string, precision = FEE_RATE_PRECISION): string {
  return minorToPercent(rate, precision) || "0";
}

/** A typed percentage as the wire rate at precision 6 ("0.25" → "2500"). */
export function percentToRate(percent: string): string {
  return percentToMinor(percent, FEE_RATE_PRECISION);
}

/** A rate for a table cell: "0.25%", or "—" when there is none. */
export function formatRate(rate: string | undefined | null, precision = FEE_RATE_PRECISION): string {
  if (rate === undefined || rate === null || !/^-?\d+$/.test(rate)) return "—";
  return `${rateToPercent(rate, precision)}%`;
}

/** Why a wire rate cannot be published, or null when broker-service would take it. */
export function feeRateProblem(rate: string): string | null {
  if (!/^-?\d+$/.test(rate)) return "Enter the rate as a percentage, for example 0.25.";
  if (rate.startsWith("-")) return "Rates cannot be negative.";
  if (Number(rate) > FEE_RATE_MAX) return "Rates cannot exceed 100%.";
  return null;
}

// ── scheduling ───────────────────────────────────────────────────────

/** The two ScheduleField modes. */
export type ScheduleMode = "now" | "scheduled";

export type ScheduleResult =
  | { ok: true; unixMs: number }
  | { ok: false; problem: "missing" | "invalid" | "past" };

/**
 * The effective time a write sends, from the schedule field: 0 = now
 * ("Immediately"), else the chosen instant in unix ms. Unlike the
 * exchange fee policies — where ScheduleField's past time simply means
 * now — broker-service refuses a past time with 400 (versions,
 * retirements and assignments are never backdated), so a past or
 * not-yet-picked time is blocked here instead of being sent.
 */
export function scheduleEffectiveAt(mode: ScheduleMode, rfc3339: string, nowMs: number): ScheduleResult {
  if (mode === "now") return { ok: true, unixMs: 0 };
  if (!rfc3339) return { ok: false, problem: "missing" };
  const ms = new Date(rfc3339).getTime();
  if (!Number.isFinite(ms)) return { ok: false, problem: "invalid" };
  if (ms <= nowMs) return { ok: false, problem: "past" };
  return { ok: true, unixMs: ms };
}

/** What the dialog says under the schedule field when the time cannot be sent. */
export function scheduleProblemText(problem: "missing" | "invalid" | "past"): string {
  switch (problem) {
    case "missing":
      return "Pick the date and time it takes effect, or choose Immediately.";
    case "invalid":
      return "That date and time could not be read. Pick it again.";
    default:
      return PAST_TIME_HINT;
  }
}

/** ScheduleField's hint for a past time on these forms: refused, never "now". */
export const PAST_TIME_HINT =
  "That time is in the past, and broker-service refuses a past time. Pick a future time, or choose Immediately.";

/** The `effective_at_unix_ms` / `retire_at_unix_ms` field: omitted for now, like the wire's omitempty. */
export function scheduledField<K extends string>(key: K, unixMs: number): Partial<Record<K, number>> {
  return unixMs > 0 ? ({ [key]: unixMs } as Record<K, number>) : {};
}

// ── outcomes and refusals ────────────────────────────────────────────

/**
 * broker-service's coded 503: market-service could not answer the
 * redemption-ceiling check, which runs BEFORE the transaction — so
 * nothing was written and the form stays editable.
 */
export function isStateUnavailable(e: unknown): boolean {
  return e instanceof APIError && e.status === 503 && e.code === "BROKER_STATE_UNAVAILABLE";
}

/**
 * The fee and share dialogs' lock rule. These writes carry no
 * Idempotency-Key: a retry after a lost response creates a second plan
 * or version. So a network error, a timeout or any 5xx leaves the
 * outcome UNKNOWN and confirm stays locked until the dialog is closed
 * and the list re-read — except the coded 503 BROKER_STATE_UNAVAILABLE,
 * which is a known no-write. A coded 4xx is a definite refusal.
 */
export function feeOutcomeUnknown(e: unknown): boolean {
  if (isStateUnavailable(e)) return false;
  return outcomeUnknown(e);
}

/** The refusal's details.rule, when broker-service named one. */
export function refusalRule(e: unknown): string | undefined {
  const rule = brokerErrorDetail(e)?.details?.rule;
  return typeof rule === "string" && rule !== "" ? rule : undefined;
}

/** Whose rules a dialog explains: a 503 means market-service only on the fee path. */
export type RefusalContext = "fee" | "share";

type Explained = { message: string };

const FEE_OWNER_RULES: Record<string, Explained> = {
  NOT_ROOT: {
    message: "Fee plans are owned and assigned by a root broker; a sub-broker cannot own or assign rates.",
  },
  PLAN_OF_ANOTHER_ROOT: { message: "This fee plan belongs to another root broker." },
  TARGET_OUTSIDE_TREE: { message: "The assignment target is not in this root broker's tree." },
  GROUP_TARGET_NOT_ROOT: { message: "Only the root itself can be the target of a GROUP assignment." },
};

const FEE_IMMUTABLE_RULES: Record<string, Explained> = {
  SYSTEM_BROKER: {
    message: "The SYSTEM_BROKER fee plan is protected: native users are charged exactly today's fee.",
  },
  PROTECTED_PLAN: { message: "This fee plan is protected: it takes no version, assignment or retirement." },
  PLAN_RETIRED: { message: "The fee plan is retired at that time, so it cannot take a new version or assignment." },
  ALREADY_RETIRED: { message: "This fee plan is already retired. Plans retire only once." },
  PLAN_RETIRING: {
    message:
      "This fee plan has a retirement scheduled. Any rule naming it would refuse every order and GOLD redemption under the scope from then, so it cannot be assigned. Assign another plan.",
  },
};

/** Why no fee plan resolves (BROKER_FEE_ASSIGNMENT_MISSING details.rule, or the schedule's missing_rule). */
const MISSING_RULES: Record<string, Explained> = {
  NO_ASSIGNMENT: {
    message:
      "No fee plan is assigned on this path: not to the node, to a branch above it, or to the group. Orders and GOLD redemptions (manual and auto-redeem) are refused.",
  },
  PLAN_RETIRED: {
    message:
      "The plan assigned at the nearest rule is retired. Fee rules never fall back, so orders and GOLD redemptions (manual and auto-redeem, positions already held included) are refused until that scope is reassigned.",
  },
  NO_VERSION_IN_CURRENCY: {
    message:
      "The plan assigned at the nearest rule has no version in this currency at this time. Fee rules never fall back to a branch or the group, so orders in this currency are refused, and in GOLD so are redemptions.",
  },
  PLAN_UNKNOWN: { message: "The nearest rule names a fee plan that does not exist." },
  PLAN_OWNER_MISMATCH: { message: "The nearest rule names a fee plan owned by another root broker." },
  EMPTY_PATH: { message: "The broker path is empty, so no rule can apply." },
};

const SHARE_INVALID_RULES: Record<string, Explained> = {
  NO_SHARES: { message: "Each version must name at least one recipient." },
  SHARE_NOT_POSITIVE: { message: "Every share must be between 1 and 10000 bps." },
  SHARES_NOT_10000: { message: "The shares must total exactly 10000 bps (100%)." },
  RECIPIENT_REPEATED: { message: "Each recipient may appear only once." },
  RECIPIENT_INVALID: { message: "Each recipient must be a broker id or @servicing." },
  REMAINDER_NOT_RECIPIENT: { message: "The remainder recipient must also hold a share." },
  RECIPIENT_OUTSIDE_TREE: { message: "Every recipient must be a broker in this root's tree." },
  RECIPIENT_CLOSED: { message: "Closed brokers cannot receive a share." },
  OFF_PATH: {
    message:
      "An explicit recipient is off the path: every named recipient must be the assignment target or one of its ancestors, or orders under it are refused.",
  },
  SERVICING_NAMED_TWICE: {
    message:
      "The version has an @servicing share and also names the assignment target, so the target's own orders would name it twice. Keep only one of the two.",
  },
  NOT_ROOT: {
    message: "Revenue-share plans are owned and assigned by a root broker; a sub-broker cannot own one.",
  },
  SYSTEM_BROKER: { message: "The SYSTEM_BROKER always uses the default split: 100% to the servicing broker." },
  PLAN_OF_ANOTHER_ROOT: { message: "This revenue-share plan belongs to another root broker." },
  TARGET_OUTSIDE_TREE: { message: "The assignment target is not in this root broker's tree." },
  GROUP_TARGET_NOT_ROOT: { message: "Only the root itself can be the target of a GROUP assignment." },
};

const SHARE_CONFLICT_RULES: Record<string, Explained> = {
  PLAN_RETIRED: {
    message: "The revenue-share plan is retired at that time, so it cannot take a new version or assignment.",
  },
  ALREADY_RETIRED: { message: "This revenue-share plan is already retired. Plans retire only once." },
  DUPLICATE_EFFECTIVE_AT: {
    message:
      "An assignment for the same scope, target and effective time already exists. The list was refreshed; choose another time.",
  },
};

const REASON_RULES: Record<string, Explained> = {
  REASON_REQUIRED: { message: "A reason is required" },
};

/** Codes whose meaning does not depend on a rule. */
const CODE_TEXT: Record<string, Explained> = {
  BROKER_FEE_ASSIGNMENT_CONFLICT: {
    message:
      "An assignment for the same scope, target and effective time already exists. The list was refreshed; choose another time.",
  },
  BROKER_FEE_CEILING_EXCEEDED: {
    message:
      "The exchange's highest redemption rate plus this broker redemption rate would exceed 100%. The version is rejected, never capped.",
  },
  BROKER_FEE_CURRENCY_NOT_TRADED: {
    message:
      "No broker in this tree trades that currency, so a version in it could never apply. Publish only in the currencies the dialog offers; versions already published in other currencies stay listed as history.",
  },
  BROKER_FEE_PLAN_IN_USE: {
    message:
      "The plan is still assigned at the scopes listed below. Retiring it would refuse every order and every GOLD redemption (manual and auto-redeem) under them. Reassign or clear each scope from the retire time or earlier, then retire the plan.",
  },
  BROKER_PAUSED: {
    message: "The root broker is paused. Fee and share changes need the root to be DRAFT or ACTIVE.",
  },
  BROKER_CLOSED: {
    message: "The root broker is closing or closed. Fee and share changes need the root to be DRAFT or ACTIVE.",
  },
};

const RATE_INVALID: Explained = {
  message: "Each rate must be between 0% and 100% at precision 6 (\"10000\" is 1%).",
};
const RATE_INVALID_REDEMPTION: Explained = {
  message: "RED and GREEN versions must carry a redemption rate of 0: redemption fees apply on the GOLD path only.",
};
const STATE_UNAVAILABLE_FEE: Explained = {
  message: "market-service is unavailable, so the redemption ceiling could not be checked and nothing was saved. Try again in a moment.",
};
const STATE_UNAVAILABLE_SHARE: Explained = {
  message: "broker-service could not read a state this change depends on, so nothing was saved. Try again in a moment.",
};

/**
 * A refusal in words an operator can act on, by code and details.rule —
 * or null when the code or rule is not one these screens know, in which
 * case the notice shows the raw code and rule instead.
 */
export function refusalExplanation(e: unknown, context: RefusalContext = "fee"): string | null {
  if (!(e instanceof APIError)) return null;
  const rule = refusalRule(e);
  const byRule = (table: Record<string, Explained>) => (rule ? table[rule]?.message ?? null : null);
  switch (e.code) {
    case "BROKER_FEE_OWNER_MISMATCH":
      return byRule(FEE_OWNER_RULES);
    case "BROKER_FEE_PLAN_IMMUTABLE":
      return byRule(FEE_IMMUTABLE_RULES);
    case "BROKER_FEE_ASSIGNMENT_MISSING":
      return byRule(MISSING_RULES);
    case "BROKER_REVENUE_SHARE_INVALID":
      return byRule(SHARE_INVALID_RULES);
    case "BROKER_REVENUE_SHARE_CONFLICT":
      return byRule(SHARE_CONFLICT_RULES);
    case "BROKER_LIFECYCLE_TRANSITION_INVALID":
      return byRule(REASON_RULES);
    case "BROKER_FEE_RATE_INVALID": {
      const d = brokerErrorDetail(e)?.details;
      return d?.field === "redemption_fee_rate" && d?.currency && d.currency !== "GOLD"
        ? RATE_INVALID_REDEMPTION.message
        : RATE_INVALID.message;
    }
    case "BROKER_STATE_UNAVAILABLE":
      return context === "fee" ? STATE_UNAVAILABLE_FEE.message : STATE_UNAVAILABLE_SHARE.message;
    default:
      return CODE_TEXT[e.code]?.message ?? nodeCurrencyRefusal(e);
  }
}

/**
 * The scopes a BROKER_FEE_PLAN_IN_USE refusal names (details.scopes,
 * "SCOPE:broker_id" comma list, SM-864) — the assignments that still name
 * the plan; empty for any other error.
 */
export function planInUseScopes(e: unknown): string[] {
  if (!(e instanceof APIError) || e.code !== "BROKER_FEE_PLAN_IN_USE") return [];
  const scopes = brokerErrorDetail(e)?.details?.scopes;
  return typeof scopes === "string" && scopes !== "" ? scopes.split(",") : [];
}

/** Why no plan resolves, from the schedule's missing_rule; null for a rule these screens do not know. */
export function missingRuleText(rule: string | undefined): string | null {
  return rule ? MISSING_RULES[rule]?.message ?? null : null;
}

/** The explanation of one share rule (the schedule's 422, the editor's own checks). */
export function shareRuleText(rule: string | undefined): string | null {
  return rule ? SHARE_INVALID_RULES[rule]?.message ?? null : null;
}

/** The broken-share read every fee schedule of the node fails with (422, 708 §10 (i)). */
export function isShareVersionBroken(e: unknown): boolean {
  return e instanceof APIError && e.code === "BROKER_REVENUE_SHARE_INVALID";
}

// ── the redemption ceiling ───────────────────────────────────────────

/** A rate with the precision it is written at. */
export interface ScaledRate {
  rate: string;
  precision: number;
}

/** What a BROKER_FEE_CEILING_EXCEEDED refusal says, with the highest rate that would pass. */
export interface CeilingRefusal {
  currency: string;
  /** The highest redemption rate any active or scheduled exchange profile can apply. */
  exchangeMax: ScaledRate;
  /** The broker redemption rate that was refused. */
  entered: ScaledRate;
  /** 100 % at the broker precision. */
  maxCombined: ScaledRate;
  /** max_combined_fee_rate − exchange_max_redemption_fee_rate, at the finer of the two precisions, never below 0. */
  highestAllowed: ScaledRate;
}

const DIGITS = /^(0|[1-9][0-9]*)$/;

function precisionOf(v: string | undefined, fallback?: number): number | null {
  if (v === undefined || v === "") return fallback ?? null;
  if (!DIGITS.test(v)) return null;
  const n = Number(v);
  return n >= 0 && n <= 18 ? n : null;
}

/**
 * The details of a BROKER_FEE_CEILING_EXCEEDED refusal (broker-service
 * checkRedemptionCeiling), or null when the error is not one or its
 * details cannot be read. The highest allowed broker rate is computed
 * exactly — integers scaled to the finer precision, never a float — the
 * way broker-service compares the sum.
 */
export function parseCeilingRefusal(e: unknown): CeilingRefusal | null {
  const d = brokerErrorDetail(e);
  if (!d || d.code !== "BROKER_FEE_CEILING_EXCEEDED") return null;
  const det = d.details ?? {};
  const exchangePrecision = precisionOf(det.exchange_fee_rate_precision);
  const brokerPrecision = precisionOf(det.fee_rate_precision, FEE_RATE_PRECISION);
  const exchangeMax = det.exchange_max_redemption_fee_rate ?? "";
  const entered = det.broker_redemption_fee_rate ?? "";
  const maxCombined = det.max_combined_fee_rate ?? String(FEE_RATE_MAX);
  if (exchangePrecision === null || brokerPrecision === null) return null;
  if (!DIGITS.test(exchangeMax) || !DIGITS.test(maxCombined) || (entered !== "" && !DIGITS.test(entered))) return null;
  const finer = Math.max(exchangePrecision, brokerPrecision);
  const scale = (v: string, from: number) => BigInt(v) * 10n ** BigInt(finer - from);
  const allowed = scale(maxCombined, brokerPrecision) - scale(exchangeMax, exchangePrecision);
  return {
    currency: det.currency ?? "",
    exchangeMax: { rate: exchangeMax, precision: exchangePrecision },
    entered: { rate: entered || "0", precision: brokerPrecision },
    maxCombined: { rate: maxCombined, precision: brokerPrecision },
    highestAllowed: { rate: (allowed < 0n ? 0n : allowed).toString(), precision: finer },
  };
}

// ── plans and versions ───────────────────────────────────────────────

/** The plan may apply (take a version, be assigned) at `atMs`: never retired, or retired strictly later. */
export function planUsableAt(plan: Pick<FeePlanItem, "retired_at_unix_ms">, atMs: number): boolean {
  return !plan.retired_at_unix_ms || plan.retired_at_unix_ms > atMs;
}

function laterVersion(a: FeePlanVersionItem, b: FeePlanVersionItem): boolean {
  if (a.effective_at_unix_ms !== b.effective_at_unix_ms) return a.effective_at_unix_ms > b.effective_at_unix_ms;
  if (a.version !== b.version) return a.version > b.version;
  return a.plan_version_id > b.plan_version_id;
}

/** One currency's versions of a plan, as the detail shows them. */
export interface CurrencyVersions {
  currency: string;
  /** In effect now: the greatest effective time not after now, then the highest version. */
  current?: FeePlanVersionItem;
  /** Effective later, soonest first. */
  scheduled: FeePlanVersionItem[];
  /** Effective before and replaced by `current`, newest first. */
  superseded: FeePlanVersionItem[];
}

/**
 * The currencies a new version of the plan may be published in: the
 * detail read's tree_currencies (SM-851; broker-service refuses any other
 * with BROKER_FEE_CURRENCY_NOT_TRADED), in GOLD, RED, GREEN order. A
 * plan read without the field (a list row, an older server) falls back
 * to the node rule (SM-866): GOLD for a broker tree, all three for the
 * SYSTEM_BROKER's. The SYSTEM_BROKER's tree always trades all three — the
 * only one in game mode — whatever an older server lists for it, so its
 * zero plan never shows a currency as history.
 */
export function tradedCurrencies(
  plan: Pick<FeePlanItem, "tree_currencies" | "root_broker_id"> | undefined | null,
): BrokerCurrency[] {
  const system = plan?.root_broker_id === SYSTEM_BROKER_ID;
  if (system) return [...BROKER_CURRENCIES];
  return nodeCurrencies({ currencies: plan?.tree_currencies });
}

/**
 * A plan's versions per currency, split into current, scheduled and
 * superseded at `nowMs`: every currency in `currencies` (default GOLD,
 * RED, GREEN), then any other currency the plan has versions in — kept
 * as history (SM-863), never offered for a new version. This reads one
 * plan's own data; it is not a resolution walk — whether the plan
 * applies to a node is the fee schedule's answer.
 */
export function groupFeeVersions(
  versions: FeePlanVersionItem[],
  nowMs: number,
  currencies: readonly string[] = BROKER_CURRENCIES,
): CurrencyVersions[] {
  const byCurrency = new Map<string, FeePlanVersionItem[]>();
  for (const v of versions) byCurrency.set(v.currency, [...(byCurrency.get(v.currency) ?? []), v]);
  const rank = (c: string) => {
    const i = (BROKER_CURRENCIES as readonly string[]).indexOf(c);
    return i < 0 ? BROKER_CURRENCIES.length : i;
  };
  const history = [...byCurrency.keys()]
    .filter((c) => !currencies.includes(c))
    .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  const order = [...currencies, ...history];
  return order.map((currency) => {
    const all = byCurrency.get(currency) ?? [];
    let current: FeePlanVersionItem | undefined;
    for (const v of all) {
      if (v.effective_at_unix_ms <= nowMs && (!current || laterVersion(v, current))) current = v;
    }
    const scheduled = all
      .filter((v) => v.effective_at_unix_ms > nowMs)
      .sort((a, b) => (laterVersion(a, b) ? 1 : -1));
    const superseded = all
      .filter((v) => v.effective_at_unix_ms <= nowMs && v !== current)
      .sort((a, b) => (laterVersion(a, b) ? -1 : 1));
    return { currency, current, scheduled, superseded };
  });
}

/**
 * The currencies the plan has a version in, effective at or before
 * `atMs` — for the assignment dialog's coverage warning. Plan data only:
 * a missing currency means orders in it under that scope are refused
 * (NO_VERSION_IN_CURRENCY); nothing here decides what a node resolves.
 */
export function currenciesCoveredAt(versions: Pick<FeePlanVersionItem, "currency" | "effective_at_unix_ms">[], atMs: number): string[] {
  const covered = new Set(versions.filter((v) => v.effective_at_unix_ms <= atMs).map((v) => v.currency));
  return BROKER_CURRENCIES.filter((c) => covered.has(c));
}

/**
 * The GOLD redemption rate in effect now (precision 6), or null when no
 * GOLD version is in effect — what a new version's redemption rate is
 * compared with (SM-865: a higher rate also applies to positions already
 * held, since a redemption is priced when the user redeems).
 */
export function currentGoldRedemptionRate(versions: FeePlanVersionItem[], nowMs: number): ScaledRate | null {
  const gold = groupFeeVersions(versions, nowMs, ["GOLD"]).find((g) => g.currency === "GOLD")?.current;
  return gold ? { rate: gold.redemption_fee_rate, precision: gold.fee_rate_precision } : null;
}

/** Whether `next` (precision 6) is a higher rate than `current`, compared exactly. */
export function redemptionRateRises(next: string, current: ScaledRate | null): boolean {
  if (!current || !/^\d+$/.test(next) || !/^\d+$/.test(current.rate)) return false;
  const p = Math.max(FEE_RATE_PRECISION, current.precision);
  const scale = (rate: string, precision: number) => BigInt(rate) * 10n ** BigInt(p - precision);
  return scale(next, FEE_RATE_PRECISION) > scale(current.rate, current.precision);
}

// ── who may change a root's plans ────────────────────────────────────

/** Whether a tab's write buttons show, whether they are live, and why not. */
export interface WriteGate {
  /** Draw the write buttons at all. */
  show: boolean;
  /** Draw them enabled. */
  enabled: boolean;
  /** The read-only note, when there is one. */
  note: string | null;
}

/** The root statuses broker-service accepts a fee or share change in (FEE_CHANGE). */
const CHANGEABLE: readonly string[] = ["DRAFT", "ACTIVE"];

/** Why a root's plans cannot change in its status (the read-only note). */
const FROZEN: Record<string, { message: string }> = {
  PAUSED: { message: "This root is paused: fee and share plans change only while the root is DRAFT or ACTIVE." },
  CLOSING: { message: "This root is closing: fee and share plans change only while the root is DRAFT or ACTIVE." },
  CLOSED: { message: "This root is closed: fee and share plans change only while the root is DRAFT or ACTIVE." },
};
const FROZEN_OTHER = { message: "Fee and share plans change only while the root is DRAFT or ACTIVE." };

/**
 * The write gate of a root's Fees or Revenue share tab. The SYSTEM_BROKER
 * takes no change at all (no buttons); without the manage permission the
 * tab is read-only (no buttons); on a PAUSED, CLOSING or CLOSED root the
 * buttons show but stay disabled, because broker-service answers
 * BROKER_PAUSED / BROKER_CLOSED until it is DRAFT or ACTIVE again.
 */
export function writeGate(
  root: Pick<BrokerItem, "broker_id" | "root_broker_id" | "is_system" | "status" | "effective_status">,
  canManage: boolean,
  copy: { system: string; permission: string },
): WriteGate {
  if (isSystemBroker(root)) return { show: false, enabled: false, note: copy.system };
  if (!canManage) return { show: false, enabled: false, note: copy.permission };
  const status = root.effective_status ?? root.status;
  if (!CHANGEABLE.includes(status)) {
    return { show: true, enabled: false, note: (FROZEN[status] ?? FROZEN_OTHER).message };
  }
  return { show: true, enabled: true, note: null };
}

// ── scopes ───────────────────────────────────────────────────────────

const SCOPES: Record<FeeAssignmentScope, { label: string; help: string }> = {
  GROUP: { label: "Group", help: "The whole tree. The target is the root." },
  BRANCH: { label: "Branch", help: "The target node and every node beneath it." },
  NODE: { label: "Node", help: "The target node only." },
};

export function scopeLabel(scope: string): string {
  return SCOPES[scope as FeeAssignmentScope]?.label ?? scope;
}

export function scopeHelp(scope: string): string {
  return SCOPES[scope as FeeAssignmentScope]?.help ?? "";
}
